// What the parameter mapping card SHOWS, which is not always what the project STORES.
//
// A project whose classification has never been chosen has no `project_speckle_parameter_config` row, and
// the wrapper reads that absence as two empty strings. The card used to fill them in from the country's
// defaults on mount — a write, into a project nobody had touched, landing after the settings page had
// already taken the baseline it compares against. The rail then reported "1 section edited, not saved yet"
// and wore a green complete check on a project created a minute earlier (RTLCA-5782).
//
// So the default is resolved for display, and only a real choice writes. A default nobody picked is a
// suggestion, and a suggestion does not belong in the saved payload — the API applies the same fallback
// server-side when the row is absent, so leaving it unsaved changes no result.
//
// Pure over the store's array rather than reading the store itself, so callers can hold it in a computed:
// the classification types arrive from an async query, and a one-off read at mount was a race it could
// lose, leaving both selects blank.

// Providers
import type { ClassificationTypesType } from "@/dataProviders/ClassificationTypes";

// Types
import type { SpeckleConfigurationType } from "../types/SpeckleConfigurationType";

export type ClassificationSelectionType = {
  classificationTypeId: string;
  classificationCode: string;
};

/** The parameter a classification system nominates as its own default, or `""` when it nominates none. */
export const defaultClassificationParameter = (classificationTypes: ClassificationTypesType, typeId: string): string => {
  if (!typeId) return "";

  const classificationType = classificationTypes.find((entry) => entry?.classificationType?.id === typeId);
  return classificationType?.classificationType?.parameters?.find((parameter) => parameter?.isDefault)?.value ?? "";
};

/**
 * The classification system and parameter to show for a project: its own where it has them, the country's
 * defaults where it does not.
 *
 * The two fields fall back independently, so a project that stores a system but no parameter still shows
 * that system's default rather than an empty select. Blank means "not chosen" on both sides — the wrapper
 * writes `""` for an absent row, and no classification system or parameter is named by the empty string.
 */
export const resolveClassificationDefaults = (
  classificationTypes: ClassificationTypesType,
  speckleConfiguration: SpeckleConfigurationType | null | undefined
): ClassificationSelectionType => {
  const storedTypeId = speckleConfiguration?.parameters?.classificationTypeId ?? "";
  const storedCode = speckleConfiguration?.parameters?.classificationCode ?? "";

  const classificationTypeId =
    storedTypeId || classificationTypes.find((entry) => entry?.isDefault)?.classificationType?.id || "";

  return {
    classificationTypeId,
    classificationCode: storedCode || defaultClassificationParameter(classificationTypes, classificationTypeId),
  };
};

export default resolveClassificationDefaults;
